import React from 'react';
import { Info } from 'lucide-react';
import { useBloodTestData, useBloodTestInfo } from '../context/BloodTestDataContext';

// Simple sparkline component using SVG
const Sparkline = ({ data }) => {
  if (!data || data.length < 2) return <div className="w-16 h-8" />;

  const values = data.map(d => d.value).filter(v => v !== null && v !== undefined);
  if (values.length < 2) return <div className="w-16 h-8" />;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min;
  const width = 64;
  const height = 32;
  const padding = 2;

  // Generate SVG path
  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * (width - 2 * padding) + padding;
    const y = height - padding - ((value - min) / (range || 1)) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');

  // Determine color based on trend
  const firstValue = values[0];
  const lastValue = values[values.length - 1];
  const isAbnormal = data[data.length - 1]?.is_abnormal;
  const lineColor = isAbnormal ? '#ef4444' : (lastValue > firstValue ? '#10b981' : '#3b82f6');

  return (
    <svg width={width} height={height} className="inline-block">
      <polyline
        points={points}
        fill="none"
        stroke={lineColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default function SubcategoryTablePanel({ category, subcategory, results }) {
  const { selectedPatientId, getTestTimeSeries } = useBloodTestData();
  const { getTestDefinition } = useBloodTestInfo();

  // Get all test names in this subcategory
  const testNames = Object.keys(results).sort();

  // Get all unique dates across all tests in this subcategory, sorted descending
  const getAllDates = () => {
    const allDates = new Set();
    testNames.forEach(testName => {
      const timeSeries = getTestTimeSeries(selectedPatientId, testName);
      timeSeries.forEach(point => {
        if (point.date) {
          allDates.add(point.date);
        }
      });
    });
    return Array.from(allDates).sort((a, b) => new Date(b) - new Date(a));
  };

  const allDates = getAllDates();

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Get value for a specific test on a specific date
  const getValue = (testName, date) => {
    const timeSeries = getTestTimeSeries(selectedPatientId, testName);
    const point = timeSeries.find(p => p.date === date);
    return point;
  };

  if (testNames.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900">{subcategory}</h4>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse border border-gray-400">
          <thead>
            <tr className="bg-gray-300 border-b border-gray-400">
              <th className="sticky left-0 z-10 bg-gray-300 px-4 py-3 text-left font-semibold text-gray-900 border-r border-gray-400 w-64">
                Diagnostic
              </th>
              <th className="bg-gray-300 px-4 py-3 text-center font-semibold text-gray-900 border-r border-gray-400 w-24">
                Trend
              </th>
              {allDates.map((date) => (
                <th key={date} className="bg-gray-300 px-4 py-3 text-center font-semibold text-gray-900 min-w-[100px] border-r border-gray-400">
                  {formatDate(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {testNames.map((testName) => {
              const testData = getTestTimeSeries(selectedPatientId, testName);
              const testDef = getTestDefinition(testName);
              const description = testDef?.short_description || testDef?.full_description || 'No description available';

              return (
                <tr key={testName} className="hover:bg-green-50 transition-colors">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-gray-200 hover:bg-green-50 w-64">
                    {/* Test name with tooltip */}
                    <div className="flex items-center gap-1 relative group">
                      <span className="font-medium text-gray-900">{testName}</span>
                      <Info className="h-4 w-4 text-gray-400 flex-shrink-0 cursor-help" />
                      {/* Tooltip - positioned above */}
                      <div className="absolute left-0 bottom-full mb-2 w-64 bg-blue-100 text-black text-base rounded-lg p-3 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 pointer-events-none">
                        {description}
                        {/* Arrow pointing down */}
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-100"></div>
                      </div>
                    </div>
                  </td>
                  <td className="bg-white px-4 py-3 border-r border-gray-200 text-center hover:bg-green-50 w-24">
                    {/* Sparkline */}
                    <Sparkline data={testData} />
                  </td>
                  {allDates.map((date) => {
                    const point = getValue(testName, date);

                    if (!point) {
                      return (
                        <td key={date} className="px-4 py-3 text-center text-gray-300 border-r border-gray-200">
                          —
                        </td>
                      );
                    }

                    const isAbnormal = point.isAbnormal;
                    const value = point.value;
                    const unit = point.unit || '';
                    let normalMin = point.normalRangeMin;
                    let normalMax = point.normalRangeMax;

                    // Fall back to typical range from test definition if actual range is missing
                    if ((normalMin == null || normalMax == null) && testDef) {
                      // Try to parse typical range (format: "2.20-2.60" or "40-160")
                      const typicalRange = testDef.typical_range_male || testDef.typical_range_female;
                      if (typicalRange && typicalRange.includes('-') && !typicalRange.includes('varies')) {
                        const [rangeMin, rangeMax] = typicalRange.split('-').map(s => parseFloat(s.trim()));
                        if (normalMin == null && !isNaN(rangeMin)) normalMin = rangeMin;
                        if (normalMax == null && !isNaN(rangeMax)) normalMax = rangeMax;
                      }
                    }

                    // Format reference range
                    let rangeText = '';
                    if (normalMin != null && normalMax != null) {
                      rangeText = `${normalMin}-${normalMax}`;
                    } else if (normalMin != null) {
                      rangeText = `>${normalMin}`;
                    } else if (normalMax != null) {
                      rangeText = `<${normalMax}`;
                    }

                    return (
                      <td key={date} className="px-4 py-3 text-center border-r border-gray-200">
                        <div className={`font-semibold ${isAbnormal ? 'text-red-600' : 'text-gray-900'}`}>
                          {value} {unit}
                        </div>
                        {rangeText && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {rangeText}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
